<script setup lang="ts">
  import type { ListPermissionsModalPayload } from './ListPermissionsModal.types';
  import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useModal } from 'vue-final-modal';
  import { IconImage, SearchNoResults, TableLoading, DefaultTable } from '@/shared/ui';
  import SaveAndSort from './SaveAndSort.vue';
  import Arrow from '@/shared/assets/icons/Arrow.svg';
  import {
    permissionApi,
    PermissionMatrixPositionType,
    usePermissionsMatrix,
  } from '@/entities/Permission';
  import { usePermissions } from '@/pages/admin/lib';
  import PermissionsMatrixTable from './PermissionsMatrixTable.vue';
  import { PERMISSIONS_ORDER_OPTIONS } from '@/shared/config';
  import { isSortOrder, type SortOrder } from '@/shared/lib';
  import { usePermissionsExport } from './usePermissionsExport';
  import PermissionsHeader from './PermissionsHeader.vue';
  import {
    firstQueryValue,
    useCustomToast,
    useInfiniteScrollObserver,
    useUpdateUrl,
  } from '@/shared/lib';
  import ListPermissionsModal from './ListPermissionsModal.vue';

  const INITIAL_VISIBLE_CATEGORIES = 3;
  const CATEGORY_BATCH_SIZE = 2;

  const POSITION_TYPE_QUERY_KEY = 'PositionType';
  const POSITION_TYPE_OPTIONS = [
    { id: 'POSITION', text: 'Cargos' },
    { id: 'FUNCTIONAL_ACTIVITY', text: 'Atividades Funcionais' },
  ];

  const router = useRouter();
  const route = useRoute();
  const toast = useCustomToast();
  const { updateUrl } = useUpdateUrl();

  const pageContainer = ref<HTMLElement | null>(null);
  const scrollContainer = ref<HTMLElement | null>(null);
  const selectedCategoryFilter = ref<number | null>(null);
  const showScrollTopButton = ref(false);
  const SCROLL_TOP_THRESHOLD = 240;
  const currentSort = ref<{ categoryId: number; sort: string; order: SortOrder } | null>(null);
  const visibleCategoryCount = ref(INITIAL_VISIBLE_CATEGORIES);
  const loadingMoreCategories = ref(false);
  const isListPermissionsModalOpen = ref(false);
  const lastListPermissionsFilter = ref<ListPermissionsModalPayload | null>(null);

  const SORT_FIELDS = new Set(PERMISSIONS_ORDER_OPTIONS.map((option) => option.sort));

  const readPositiveQueryNumber = (value: unknown): number | null => {
    const rawValue = firstQueryValue(value);

    if (!rawValue) {
      return null;
    }

    const parsed = Number(rawValue);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  };

  const syncUrl = async (positionType = selectedPositionType.value) => {
    await updateUrl({
      search: searchTerm.value.trim(),
      sort: currentSort.value
        ? { sort: currentSort.value.sort, order: currentSort.value.order }
        : undefined,
      query: {
        [POSITION_TYPE_QUERY_KEY]: positionType,
        criterion: searchCriterion.value,
        categoryId: selectedCategoryFilter.value,
        sortCategoryId: currentSort.value?.categoryId,
      },
    });
  };

  const hydrateFromQuery = () => {
    searchTerm.value = firstQueryValue(route.query.term) ?? '';

    const criterion = firstQueryValue(route.query.criterion);
    searchCriterion.value = criterion === 'positions' ? 'positions' : 'permissions';

    selectedCategoryFilter.value = readPositiveQueryNumber(route.query.categoryId);

    const sort = firstQueryValue(route.query.sort);
    const order = firstQueryValue(route.query.order);
    const sortCategoryId = readPositiveQueryNumber(route.query.sortCategoryId);

    // The sort pair reaches the backend untouched through the Excel export, so an unknown
    // field would travel to the API as an orderBy key.
    currentSort.value =
      sort && SORT_FIELDS.has(sort) && isSortOrder(order) && sortCategoryId
        ? {
            categoryId: sortCategoryId,
            sort,
            order,
          }
        : null;
  };

  const {
    categories,
    isLoading,
    isSaving,
    error,
    fetchPermissionsMatrix,
    togglePermission,
    getPositionPermissionIds,
    hasPendingChanges,
    saveCategoryChanges,
    discardCategoryChanges,
    sortCategory,
  } = usePermissionsMatrix();

  const {
    searchTerm,
    searchCriterion,
    handleSearch,
    handleSearchCriterionChange,
    filteredCategories,
  } = usePermissions();

  const parsePositionType = (value: unknown): PermissionMatrixPositionType => {
    return firstQueryValue(value) === 'FUNCTIONAL_ACTIVITY' ? 'FUNCTIONAL_ACTIVITY' : 'POSITION';
  };

  const selectedPositionType = computed<PermissionMatrixPositionType>(() =>
    parsePositionType(route.query[POSITION_TYPE_QUERY_KEY])
  );

  const hasPositionTypeQuery = computed(() => {
    return firstQueryValue(route.query[POSITION_TYPE_QUERY_KEY]) !== undefined;
  });

  const categoryOptions = computed(() => {
    return categories.value.map((cat) => ({
      value: cat.id,
      label: cat.name,
    }));
  });

  const searchResult = computed(() => {
    return filteredCategories(categories.value, selectedCategoryFilter.value);
  });

  const categoriesFiltered = computed(() => searchResult.value.renderedCategories);
  const emptySectionCategoryIds = computed(
    () => new Set(searchResult.value.emptySectionCategoryIds)
  );
  const hiddenCategoryIds = computed(() => searchResult.value.hiddenCategoryIds);
  const visibleCategoryIds = computed(() => {
    return new Set(categoriesFiltered.value.map((category) => category.id));
  });

  const categoriesToRender = computed(() => {
    return searchTerm.value.trim() === '' ? categories.value : categoriesFiltered.value;
  });

  const isNarrowedView = computed(() => {
    return selectedCategoryFilter.value !== null || searchTerm.value.trim() !== '';
  });

  const visibleCategories = computed(() => {
    return isNarrowedView.value
      ? categoriesToRender.value
      : categoriesToRender.value.slice(0, visibleCategoryCount.value);
  });

  const hasMoreCategories = computed(() => {
    return !isNarrowedView.value && visibleCategoryCount.value < categoriesToRender.value.length;
  });

  const hasNoResults = computed(() => {
    return (
      (!!searchTerm.value || selectedCategoryFilter.value !== null) &&
      !isLoading.value &&
      searchResult.value.hasGlobalNoResults &&
      categories.value.length > 0
    );
  });

  const { handleExportToExcel, isExporting, hasExportItems, notifySaveSuccess, notifySaveError } =
    usePermissionsExport({
      categoriesFiltered,
      searchTerm,
      selectedCategoryFilter,
      selectedPositionType,
      currentSort,
    });

  const handleTogglePermission = (positionId: number, categoryId: number, permissionId: number) => {
    togglePermission(positionId, categoryId, permissionId);
  };

  const handlePositionTypeChange = async (optionId: string) => {
    const nextPositionType =
      optionId === 'FUNCTIONAL_ACTIVITY' ? 'FUNCTIONAL_ACTIVITY' : 'POSITION';

    if (selectedPositionType.value === nextPositionType) {
      return;
    }

    // `selectedPositionType` reads back from the route, so the next type has to be passed in:
    // it only becomes the current one once this write lands.
    await syncUrl(nextPositionType);
  };

  const handleSaveCategory = async (categoryId: number) => {
    try {
      await saveCategoryChanges(categoryId);
      notifySaveSuccess();
    } catch (err) {
      notifySaveError(err instanceof Error ? err.message : undefined);
    }
  };

  const onCategorySort = (categoryId: number, sort: string, order: string) => {
    sortCategory(categoryId, sort, order);

    // Clicking the selected option again deselects it: `useSortSelection` emits an empty pair
    // and the section goes back to its original order, so the sort has to leave the URL and the
    // Excel export with it.
    currentSort.value = sort && isSortOrder(order) ? { categoryId, sort, order } : null;
    void syncUrl();
  };

  watch(searchTerm, () => {
    void syncUrl();
  });

  watch(searchCriterion, () => {
    void syncUrl();
  });

  watch(selectedCategoryFilter, () => {
    void syncUrl();
  });

  // The select cannot vet a restored `categoryId` on its own: while the matrix is in flight its
  // option list holds nothing but the "all" sentinel, so it would read every restored id as
  // invalid and emit over it. A failed fetch keeps the categories it already had, so the filter
  // is vetted only against a matrix that actually arrived.
  const dropCategoryFilterMissingFromMatrix = () => {
    if (error.value || selectedCategoryFilter.value === null) {
      return;
    }

    const filterExists = categories.value.some(
      (category) => category.id === selectedCategoryFilter.value
    );

    if (!filterExists) {
      selectedCategoryFilter.value = null;
    }
  };

  watch(
    hiddenCategoryIds,
    (categoryIds) => {
      categoryIds.forEach((categoryId) => discardCategoryChanges(categoryId));
    },
    { immediate: true }
  );

  const loadMoreCategories = async () => {
    if (!hasMoreCategories.value || loadingMoreCategories.value) return;

    loadingMoreCategories.value = true;
    visibleCategoryCount.value = Math.min(
      visibleCategoryCount.value + CATEGORY_BATCH_SIZE,
      categoriesToRender.value.length
    );
    loadingMoreCategories.value = false;
  };

  watch([selectedCategoryFilter, searchTerm, selectedPositionType], () => {
    visibleCategoryCount.value = INITIAL_VISIBLE_CATEGORIES;
  });

  watch(
    selectedPositionType,
    async (positionType) => {
      await fetchPermissionsMatrix(positionType);

      dropCategoryFilterMissingFromMatrix();

      if (currentSort.value) {
        sortCategory(currentSort.value.categoryId, currentSort.value.sort, currentSort.value.order);
      }
    },
    { immediate: true }
  );

  useInfiniteScrollObserver({
    enabled: computed(() => !isLoading.value && !error.value && hasMoreCategories.value),
    loadingMore: loadingMoreCategories,
    hasMore: hasMoreCategories,
    onLoadMore: loadMoreCategories,
    triggerId: 'permissions-category-trigger',
    rootMargin: '600px',
    minIntervalMs: 100,
  });

  const updateScrollTopButtonVisibility = () => {
    const currentScrollTop = scrollContainer.value?.scrollTop ?? window.scrollY;
    showScrollTopButton.value = currentScrollTop > SCROLL_TOP_THRESHOLD;
  };

  const scrollToTop = () => {
    const target = scrollContainer.value ?? window;
    target.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const listPermissionsModal = useModal({
    component: ListPermissionsModal,
    attrs: {
      title: 'Listar Permissões',
      onClose() {
        isListPermissionsModalOpen.value = false;
        listPermissionsModal.close();
      },
      async onApply(payload: ListPermissionsModalPayload) {
        lastListPermissionsFilter.value = payload;

        if (payload.criterion === 'registrationNumber') {
          try {
            await permissionApi.getRegistrationPermissions(payload.registrationNumber);
          } catch {
            toast.showErrorToast({
              title: 'Número de matrícula não encontrado.',
              type: 'error',
            });
            return;
          }

          isListPermissionsModalOpen.value = false;
          listPermissionsModal.close();
          router.push({
            name: 'AdminPermissoesListar',
            query: {
              criterion: payload.criterion,
              registrationNumber: payload.registrationNumber,
            },
          });
          return;
        }

        if (payload.criterion === 'position') {
          isListPermissionsModalOpen.value = false;
          listPermissionsModal.close();
          router.push({
            name: 'AdminPermissoesListar',
            query: {
              criterion: payload.criterion,
              positionIds: payload.positionIds.join(','),
            },
          });
          return;
        }

        isListPermissionsModalOpen.value = false;
        listPermissionsModal.close();
        router.push({
          name: 'AdminPermissoesListar',
          query: {
            criterion: payload.criterion,
            functionalActivityIds: payload.functionalActivityIds.join(','),
          },
        });
      },
    },
  });

  const openListPermissionsModal = () => {
    isListPermissionsModalOpen.value = true;
    listPermissionsModal.open();
  };

  defineExpose({
    isListPermissionsModalOpen,
    lastListPermissionsFilter,
    listPermissionsModal,
  });

  hydrateFromQuery();

  onMounted(async () => {
    if (!hasPositionTypeQuery.value) {
      await syncUrl();
    }
    scrollContainer.value = pageContainer.value?.closest('main') as HTMLElement | null;
    updateScrollTopButtonVisibility();
    (scrollContainer.value ?? window).addEventListener('scroll', updateScrollTopButtonVisibility);
  });

  onUnmounted(() => {
    (scrollContainer.value ?? window).removeEventListener(
      'scroll',
      updateScrollTopButtonVisibility
    );
  });
</script>

<template>
  <div ref="pageContainer">
    <PermissionsHeader
      :position-type-options="POSITION_TYPE_OPTIONS"
      :selected-position-type="selectedPositionType"
      :selected-category-filter="selectedCategoryFilter"
      :category-options="categoryOptions"
      :selected-search-criterion="searchCriterion"
      :initial-search-term="searchTerm"
      :export-disabled="isExporting || !hasExportItems"
      :export-loading="isExporting"
      @search="handleSearch"
      @update:selected-position-type="handlePositionTypeChange"
      @update:selected-category-filter="selectedCategoryFilter = $event"
      @update:selected-search-criterion="handleSearchCriterionChange"
      @export-to-excel="handleExportToExcel"
      @open-list-permissions-modal="openListPermissionsModal"
    />

    <div v-if="isLoading">
      <TableLoading :row-size="5" :column-size="4" />
    </div>

    <div v-else-if="error" class="p-8 text-center text-red-500">
      {{ error }}
    </div>

    <div v-else>
      <DefaultTable v-if="hasNoResults" class="mt-6">
        <thead>
          <tr>
            <th colspan="100" scope="colgroup">
              <div class="bg-primary-01 px-6 py-4 flex items-start justify-between">
                <h3 class="text-base font-semibold text-gray-08">Permissões</h3>
                <SaveAndSort
                  :has-changes="false"
                  :is-saving="false"
                  :order-options="PERMISSIONS_ORDER_OPTIONS"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colspan="100">
              <SearchNoResults />
            </td>
          </tr>
        </tbody>
      </DefaultTable>
      <template v-else>
        <PermissionsMatrixTable
          v-for="category in visibleCategories"
          v-show="visibleCategoryIds.has(category.id)"
          :key="category.id"
          :category="category"
          :has-changes="
            emptySectionCategoryIds.has(category.id) ? false : hasPendingChanges(category.id)
          "
          :is-saving="isSaving"
          :get-position-permission-ids="getPositionPermissionIds"
          :position-type="selectedPositionType"
          :order-options="PERMISSIONS_ORDER_OPTIONS"
          :show-empty-state="emptySectionCategoryIds.has(category.id)"
          @toggle-permission="handleTogglePermission"
          @save="handleSaveCategory"
          @update:sort="onCategorySort"
        />
      </template>
      <div
        v-if="hasMoreCategories && !hasNoResults"
        id="permissions-category-trigger"
        class="h-1"
      />
    </div>

    <button
      v-show="showScrollTopButton"
      type="button"
      class="fixed right-6 bottom-6 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-gray-02 bg-white-01 shadow-lg transition-opacity duration-200 hover:bg-gray-50"
      aria-label="Voltar ao topo"
      title="Voltar ao topo"
      @click="scrollToTop"
    >
      <IconImage :src="Arrow" class="h-5 w-5 rotate-90" alt="Voltar ao topo" />
    </button>
  </div>
</template>
